import type { PrintLegendSection } from "./exportLegend";

export interface PrintSvgPage {
	title: string;
	svg: SVGSVGElement;
	legend?: PrintLegendSection[];
}

const printStyles = `
	@page { size: A4 landscape; margin: 0; }
	* { box-sizing: border-box; }
	html, body { margin: 0; background: #e5e7eb; color: #20262f; font-family: Arial, sans-serif; }
	.sheet { width: 297mm; height: 210mm; padding: 10mm; background: #ffffff; display: grid; grid-template-rows: 10mm 1fr 7mm; break-after: page; page-break-after: always; }
	.sheet:last-child { break-after: auto; page-break-after: auto; }
	.sheet__header, .sheet__footer { display: flex; align-items: center; justify-content: space-between; }
	.sheet__header { font-size: 14pt; }
	.sheet__number, .sheet__footer { color: #5d6672; font-size: 8pt; }
	.sheet__body { min-height: 0; display: grid; }
	.sheet__drawing { min-height: 0; display: grid; place-items: center; overflow: hidden; }
	.sheet__drawing svg { width: 100%; height: 100%; display: block; }
	.sheet--legend .sheet__body { display: block; overflow: hidden; }
	.legend-table { width: 100%; border-collapse: collapse; font-size: 8pt; }
	.legend-table th, .legend-table td { border: .25mm solid #aeb7c2; padding: 1.5mm 2mm; text-align: left; vertical-align: top; }
	.legend-table thead th { background: #eef3fa; font-weight: 700; }
	.legend-table .legend-room th { background: #f7f9fc; color: #39414f; font-size: 7pt; }
	.legend-table .legend-id { width: 22mm; color: #1d4ed8; font-weight: 700; white-space: nowrap; }
	.legend-table .legend-detail { width: 64mm; color: #5d6672; }
	@media print { html, body { background: #ffffff; } }
`;

const escapeHtml = (value: string) =>
	value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");

interface LegendRow {
	room: string;
	item: PrintLegendSection["items"][number];
}

const LEGEND_ROWS_PER_SHEET = 22;

const legendRows = (sections: readonly PrintLegendSection[]): LegendRow[] =>
	sections.flatMap((section) => section.items.map((item) => ({ room: section.title, item })));

const legendSheets = (sections: readonly PrintLegendSection[]) => {
	const rows = legendRows(sections);
	const sheets: LegendRow[][] = [];
	for (let start = 0; start < rows.length; start += LEGEND_ROWS_PER_SHEET) {
		sheets.push(rows.slice(start, start + LEGEND_ROWS_PER_SHEET));
	}
	return sheets;
};

const legendSheetMarkup = (title: string, rows: readonly LegendRow[], index: number) => {
	let previousRoom = "";
	const tableRows = rows
		.map(({ room, item }) => {
			const roomHeader =
				room === previousRoom
					? ""
					: `<tr class="legend-room"><th colspan="3">${escapeHtml(room)}</th></tr>`;
			previousRoom = room;
			return `${roomHeader}<tr><td class="legend-id">${escapeHtml(item.id ?? "-")}</td><td>${escapeHtml(item.label)}</td><td class="legend-detail">${escapeHtml(item.detail)}</td></tr>`;
		})
		.join("");
	return `
		<section class="sheet sheet--legend">
			<header class="sheet__header">
				<strong>Ведомость элементов · ${escapeHtml(title)}</strong>
				<span class="sheet__number">Лист ${index}</span>
			</header>
			<main class="sheet__body"><table class="legend-table"><thead><tr><th>Поз.</th><th>Наименование</th><th>Характеристика</th></tr></thead><tbody>${tableRows}</tbody></table></main>
			<footer class="sheet__footer">Grid Planner · 2026</footer>
		</section>
	`;
};

const sheetMarkup = (page: PrintSvgPage, index: number) => `
	<section class="sheet">
		<header class="sheet__header">
			<strong>${escapeHtml(page.title)}</strong>
			<span class="sheet__number">Лист ${index}</span>
		</header>
		<main class="sheet__body"><div class="sheet__drawing">${page.svg.outerHTML}</div></main>
		<footer class="sheet__footer">Grid Planner · 2026</footer>
	</section>
`;

/** Печатает векторные SVG-листы через скрытый документ Chromium без новой вкладки. */
export const printSvgDocument = (pages: readonly PrintSvgPage[], documentTitle: string) => {
	const sheets = pages.flatMap((page) => [
		(index: number) => sheetMarkup(page, index),
		...((page.legend ?? []).length > 0
			? legendSheets(page.legend ?? []).map(
					(rows) => (index: number) => legendSheetMarkup(page.title, rows, index),
				)
			: []),
	]);
	const frame = document.createElement("iframe");
	frame.setAttribute("aria-hidden", "true");
	frame.style.cssText = "position: fixed; width: 0; height: 0; border: 0; visibility: hidden;";
	frame.srcdoc = `<!doctype html>
		<html lang="ru">
			<head><meta charset="utf-8"><title>${escapeHtml(documentTitle)}</title><style>${printStyles}</style></head>
			<body>${sheets.map((render, index) => render(index + 1)).join("")}</body>
		</html>`;
	frame.addEventListener(
		"load",
		() => {
			const printWindow = frame.contentWindow;
			if (!printWindow) return;
			printWindow.addEventListener("afterprint", () => frame.remove(), { once: true });
			printWindow.requestAnimationFrame(() => {
				printWindow.focus();
				printWindow.print();
			});
		},
		{ once: true },
	);
	document.body.append(frame);
};
